import {Component, EventEmitter, Input, Output, SimpleChanges} from '@angular/core';
import {FormsModule} from '@angular/forms';

@Component({
  selector: 'app-ad-datepicker',
  templateUrl: './ad-datepicker.component.html',
  imports: [FormsModule],
  standalone: true,
  styleUrls: ['./ad-datepicker.component.scss']
})
export class AdDatepickerComponent {
  @Input() initialDate: Date | null = null;
  @Input() minDate?: Date; // NEW: Minimum selectable date
  @Input() maxDate?: Date; // NEW: Maximum selectable date
  @Output() dateSelected = new EventEmitter<Date>();

  viewYear: number = new Date().getFullYear();
  viewMonth: number = new Date().getMonth();
  selectedDate: Date | null = null;

  weekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  ngOnInit() {
    this.setViewToInitialDate();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['initialDate'] && this.initialDate) {
      this.setViewToInitialDate();
    }
  }

  setViewToInitialDate() {
    if (this.initialDate) {
      this.selectedDate = new Date(this.initialDate);
      this.viewYear = this.selectedDate.getFullYear();
      this.viewMonth = this.selectedDate.getMonth();
    } else {
      const today = new Date();
      this.selectedDate = today;
      this.viewYear = today.getFullYear();
      this.viewMonth = today.getMonth();
    }
  }

  get daysInMonth(): number {
    return new Date(this.viewYear, this.viewMonth + 1, 0).getDate();
  }

  get days(): number[] {
    return Array.from({length: this.daysInMonth}, (_, i) => i + 1);
  }

  get monthName(): string {
    return this.monthNames[this.viewMonth];
  }

  // get yearOptions(): number[] {
  //   const currentYear = new Date().getFullYear();
  //   const years: any = [];
  //   for (let i: any = currentYear - 100; i <= currentYear + 10; i++) {
  //     years.push(i);
  //   }
  //   return years;
  // }
  get yearOptions(): number[] {
    const currentYear = new Date().getFullYear();
    let startYear = currentYear - 100;
    let endYear = currentYear + 10;
    if (this.minDate) {
      const minY = this.minDate.getFullYear();
      startYear = Math.max(startYear, minY);
    }

    if (this.maxDate) {
      const maxY = this.maxDate.getFullYear();
      endYear = Math.min(endYear, maxY);
    }

    const years: number[] = [];
    for (let y = startYear; y <= endYear; y++) {
      years.push(y);
    }
    return years;
  }

  get monthOptions(): number[] {
    return Array.from({length: 12}, (_, i) => i);
  }

  getFirstDayOfWeek(): number {
    return new Date(this.viewYear, this.viewMonth, 1).getDay();
  }

  prevMonth() {
    if (this.viewMonth === 0) {
      this.viewMonth = 11;
      this.viewYear--;
    } else {
      this.viewMonth--;
    }
  }

  nextMonth() {
    if (this.viewMonth === 11) {
      this.viewMonth = 0;
      this.viewYear++;
    } else {
      this.viewMonth++;
    }
  }

  selectDay(day: number) {
    // NEW: Check if date is disabled before selecting
    if (this.isDateDisabled(day)) {
      return; // Don't select disabled dates
    }

    this.selectedDate = new Date(this.viewYear, this.viewMonth, day);
    this.dateSelected.emit(this.selectedDate);
  }

  isSelected(day: number): boolean {
    if (!this.selectedDate) return false;
    return (
      this.selectedDate.getFullYear() === this.viewYear &&
      this.selectedDate.getMonth() === this.viewMonth &&
      this.selectedDate.getDate() === day
    );
  }

  isSaturday(day: number): boolean {
    const date = new Date(this.viewYear, this.viewMonth, day);
    return date.getDay() === 6;
  }

  // NEW: Check if a date should be disabled
  isDateDisabled(day: number): boolean {
    const date = new Date(this.viewYear, this.viewMonth, day);
    date.setHours(0, 0, 0, 0); // Normalize to start of day

    if (this.minDate) {
      const minDateNormalized = new Date(this.minDate);
      minDateNormalized.setHours(0, 0, 0, 0);
      if (date < minDateNormalized) {
        return true; // Disable dates before minDate
      }
    }

    if (this.maxDate) {
      const maxDateNormalized = new Date(this.maxDate);
      maxDateNormalized.setHours(23, 59, 59, 999);
      if (date > maxDateNormalized) {
        return true; // Disable dates after maxDate
      }
    }

    return false;
  }

  onMonthChange() {
    // Trigger change detection if needed
  }

  onYearChange() {
    // Trigger change detection if needed
  }

  goToToday() {
    const today = new Date();

    // Check if today is disabled
    this.viewYear = today.getFullYear();
    this.viewMonth = today.getMonth();

    if (!this.isDateDisabled(today.getDate())) {
      this.selectDay(today.getDate());
    }
  }

  clearSelection() {
    this.selectedDate = null;
    this.dateSelected.emit(null as any);
  }
}
